import React, { useEffect, useState } from "react";
import { XMarkIcon, MinusIcon, PlusIcon } from "@heroicons/react/24/outline";
import { TrashIcon, ShoppingBagIcon } from "@heroicons/react/24/solid";
import { Button } from "../../../../components/ui/button.tsx";
import { ScrollArea } from "../../../../components/ui/scroll-area.tsx";
import { setContrast } from '@charitystack/shared';

const freqMap = {
  MONTHLY: " /month",
  YEARLY: " /year",
  ONE_TIME: "",
  WEEKLY: " /week",
  DAILY: " /day",
  QUARTERLY: " /quarter",
  SEMI_ANNUAL: " /semi-annual",
  ANNUAL: " /year",
};

const AddToCartSheet = ({ colors }) => {
  const [checkoutPayload] = useState({
    cartItems: [
      {
        cartItemKey: "static-item",
        quantity: 1,
        amount: 100,
        frequency: "MONTHLY",
      },
    ],
  });

  let buttonColor = colors?.background || "#9A9285";
  if (colors?.background === "") {
    buttonColor = "#9A9285";
  }

  const renderCartItem = (item, index) => (
    <div
      key={`${item.cartItemKey}-${index}`}
      className={`
        relative
        my-2
        flex
        flex-col
        items-start
        gap-2.5
        overflow-hidden
        rounded-md
        border
        border-[rgba(30,30,30,0.05)]
        bg-[#FCFEFF]
        shadow-[0px_1px_1px_rgba(0,0,0,0.25)] transition-all
        duration-150
        ease-linear
      `}
    >
      <div className="flex w-full flex-row items-center justify-between bg-white p-3">
        <div className="flex flex-col gap-1">
          <div className="flex flex-col gap-0">
            <div className="open_sans truncate text-base font-normal leading-[1.48] tracking-wider text-[#1E1E1E]">
              Campaign
            </div>
            <p className="open_sans h-[24px] text-base font-normal tracking-[0.5px] text-[#7E7E7E] [font-feature-settings:'liga'_off,'clig'_off]">
              Fund
            </p>
          </div>
          {/* Quantity controls */}
          <div className="mt-1 flex items-center gap-1">
            <button
              style={{
                backgroundColor:
                  (item.quantity || 1) <= 1 ? "#D9D9D9" : buttonColor,
              }}
              className="flex h-[18px] w-[18px] items-center justify-center rounded-full p-0 transition-colors hover:brightness-90"
              disabled={(item.quantity || 1) <= 1}
            >
              <MinusIcon
                className="h-3.5 w-3.5 stroke-[2.5]"
                style={{
                  color: (item.quantity || 1) <= 1 ? "white" : "black",
                  shapeRendering: "geometricPrecision",
                }}
              />
            </button>

            <span className="open_sans min-w-[20px] text-center text-base text-[#1E1E1E]">
              {item.quantity || 1}
            </span>

            <button
              style={{
                backgroundColor: buttonColor,
              }}
              className="flex h-[18px] w-[18px] items-center justify-center rounded-full p-0 transition-colors hover:brightness-90"
            >
              <PlusIcon
                className="h-3.5 w-3.5 stroke-[2.5]"
                style={{
                  color: "white",
                  shapeRendering: "geometricPrecision",
                }}
              />
            </button>
          </div>
        </div>

        <div className="flex flex-col items-end gap-1">
          <div className="flex h-[52px] flex-col items-end justify-start">
            <span className="open_sans text-xl font-semibold tracking-[0.5px] text-[#1E1E1E] [font-feature-settings:'liga'_off,'clig'_off]">
              $100
            </span>
            <span className="open_sans h-[24px] text-base font-normal tracking-[0.5px] text-[#7E7E7E] [font-feature-settings:'liga'_off,'clig'_off]">
              {freqMap[item.frequency]}
            </span>
          </div>

          <button className="h-6 w-6 p-0 hover:bg-transparent">
            <TrashIcon className="h-4 w-4 text-[#DB3737]" />
          </button>
        </div>
      </div>
    </div>
  );

  const renderCartContent = () => (
    <>
      <ScrollArea className="w-full flex-1 pt-2">
        <div className="relative flex-1 pb-4">
          {checkoutPayload.cartItems?.length === 0 && (
            <div className="px-0">
              <p className="text-base text-ink">Your cart is empty.</p>
            </div>
          )}
          {checkoutPayload.cartItems?.map((item, index) =>
            renderCartItem(item, index)
          )}
        </div>
      </ScrollArea>

      <hr className=" mb-4 h-[1px] w-full bg-[#D9D9D9]" />

      <div className="flex w-full flex-col items-start gap-6 rounded-md border border-[rgba(30,30,30,0.05)] bg-[#FCFEFF] p-6 shadow-[0px_1px_1px_rgba(0,0,0,0.25)]">
        <div className="flex w-full flex-col items-start gap-4">
          {/* Subtotal Section */}
          <div className="flex w-full flex-col gap-3">
            <div className="flex w-full flex-row items-center justify-between">
              <span className="open_sans text-base font-normal leading-[22px] tracking-[0.5px] text-[#1E1E1E]">
                Subtotal
              </span>
              <span className="open_sans text-base font-normal leading-[22px] tracking-[0.5px] text-[#1E1E1E]">
                $
                {(
                  checkoutPayload.cartItems.reduce(
                    (total, item) => total + item.amount * (item.quantity || 1),
                    0
                  ) || 0.0
                ).toFixed(2)}
              </span>
            </div>

            <div className="h-[1px] w-full bg-[#D9D9D9]" />

            {/* Total Section */}
            <div className="flex w-full flex-row items-baseline justify-between">
              <span className="open_sans text-base font-normal leading-[22px] tracking-[0.5px] text-[#1E1E1E]">
                Total
              </span>
              <span className="open_sans text-2xl font-normal leading-[33px] tracking-[0.5px] text-[#1E1E1E]">
                $
                {(
                  checkoutPayload.cartItems.reduce(
                    (total, item) => total + item.amount * (item.quantity || 1),
                    0
                  ) || 0.0
                ).toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 flex w-full flex-col gap-4">
        {checkoutPayload.cartItems.length > 0 && (
          <Button
            style={{ 
              backgroundColor: buttonColor,
              color: setContrast(buttonColor)
            }}
            className="h-[38px] w-full rounded-md border border-edge text-base transition-colors hover:brightness-90"
          >
            Checkout
          </Button>
        )}

        <Button
          variant="outline"
          className="flex w-full items-center justify-center gap-2 self-stretch rounded-md border border-[rgba(30,30,30,0.15)] bg-paper-muted p-[10px_20px] text-base text-ink-secondary shadow-[0px_1px_1px_rgba(0,0,0,0.25)] hover:bg-paper-muted/90"
        >
          Add other items
        </Button>
      </div>
    </>
  );

  return (
    <div className="flex w-full flex-col items-center rounded-md border border-edge bg-[#F8FAFB] pb-4 pt-4">
      <div 
        className="flex h-[40px] w-[440px] flex-col items-start justify-center overflow-hidden rounded-md border border-edge-input px-4 py-2"
        style={{ backgroundColor: buttonColor }}
      >
        <div className="flex w-full justify-end">
          <div className="flex cursor-pointer items-center gap-x-1 rounded-lg">
            <ShoppingBagIcon
              className="h-6 w-6"
              style={{ color: setContrast(buttonColor) }}
            />
            <span
              className="text-2xl"
              style={{ color: setContrast(buttonColor), fontFamily: "Open Sans" }}
            >
              {`Cart (${checkoutPayload.cartItems.length})`}
            </span>
          </div>
        </div>
      </div>
      <div className="h-4" />
      <div className="flex h-[560px] w-[440px] flex-col items-start justify-between overflow-hidden rounded-md border border-edge-input bg-[#F8FAFB] p-4">
        <div className="flex w-full items-center justify-between">
          <h2 className="text-2xl font-normal text-black">Your Cart</h2>
          <button className="flex cursor-pointer items-center rounded-full bg-black/25 p-1 transition-opacity">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              height="24"
              viewBox="0 0 24 24"
              width="24"
              className="fill-white"
              style={{
                filter: "drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.25))",
              }}
            >
              <path d="M0 0h24v24H0z" fill="none" />
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
        </div>

        {renderCartContent()}
      </div>
    </div>
  );
};

export default AddToCartSheet;
